import React from 'react';
import Head from 'next/head';

import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { ResponsiveImage } from '@edx/types/responsive-image';
import { BillingPlansTable } from '@edx/components/billing-plans-table';
import { GiftBanner } from '@edx/components/banners';
import { PricingBenefits } from '@edx/components/pricing-benefits';
import { Benefit } from '@edx/components/pricing-benefits/pricing-benefits';
import { PricingComparison } from '@edx/components/pricing-comparison';
import { TestimonialsCarousel, TestimonialProps } from '@edx/components/testimonials-carousel';
import { Hero } from '@edx/components/hero';
import { PricingFAQ } from '@edx/components/pricing-faq';
import { ZendeskWidget } from '@edx/components/zendesk-widget';

type Props = {
  bgImage: ResponsiveImage;
  giftUpsellUrl: string;
  benefits: Benefit[];
  testimonials: TestimonialProps[];
};

export const PricingPage: React.FC<Props> = ({ bgImage, giftUpsellUrl, benefits, testimonials }) => {
  const { projectName, projectShortName } = useAppConfigStateContext();

  const metaTitle = 'Precio';
  const metaDescription =
    'Regálate el curso de bajo que siempre quisiste. Elije el tipo de suscripción que se adapte más a tus necesidades.';

  return (
    <>
      <Head>
        <title key="title">{metaTitle}</title>

        <meta key="description" name="description" content={metaDescription} />

        {/* Open Graph */}
        {metaTitle && <meta key="og:title" property="og:title" content={metaTitle} />}
        {metaDescription && <meta key="og:description" property="og:description" content={metaDescription} />}
      </Head>

      <Hero
        heading={`Suscríbete a ${projectShortName}`}
        subheading="Consigue acceso ilimitado a todos los contenidos, por menos de lo que cuesta una clase particular"
        contentClassName="-mt-10vh"
        bgImage={bgImage}
        noBorder
      />

      <div className="relative max-w-6xl mx-auto -mt-24 md:-mt-28 px-4 md:px-10 py-1 md:py-4 space-y-12">
        <BillingPlansTable />

        <section className="grid grid-cols-12">
          <div className="col-start-1 md:col-start-2 lg:col-start-3 col-span-12 md:col-span-10 lg:col-span-8 px-0 md:px-4">
            <GiftBanner projectName={projectName} projectShortName={projectShortName} upsellUrl={giftUpsellUrl} />
          </div>
        </section>

        <PricingBenefits benefits={benefits} />

        <PricingComparison />

        <TestimonialsCarousel
          sectionClass="max-w-2xl mx-auto pt-20 border-t border-gray-600 border-opacity-50"
          testimonials={testimonials}
        />

        <BillingPlansTable />

        <section className="grid grid-cols-12">
          <div className="col-start-1 md:col-start-2 lg:col-start-3 col-span-12 md:col-span-10 lg:col-span-8 px-0 md:px-4">
            <GiftBanner projectName={projectName} projectShortName={projectShortName} upsellUrl={giftUpsellUrl} />
          </div>
        </section>

        <PricingFAQ />
      </div>

      <ZendeskWidget />
    </>
  );
};
